import { useMemo } from 'react';
import { generateHTML, type JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { ImageWithCaption } from '../articles/editor/image-with-caption';
import { generateStaticHTML } from './static-html';

/**
 * Read-only tiptap body renderer (US-2.2/AC-4.3, PD 수첩). Converts the tiptap JSON
 * doc to static HTML with the SAME extension set the write editors use so
 * structure, images, links, highlight (형광펜), underline and tables round-trip
 * identically (WYSIWYG). Static HTML (generateHTML) keeps the read screen light;
 * the output is XSS-safe by construction.
 */
// StarterKit v3 bundles Link + Underline; disable Link so our explicit Link is
// the only one (avoids the "Duplicate extension names" warning).
const EXTENSIONS = [
  StarterKit.configure({ link: false }),
  ImageWithCaption,
  Link.configure({ openOnClick: false }),
  Highlight,
  Table,
  TableRow,
  TableHeader,
  TableCell,
];

// 서버에는 document 가 없다 — 같은 직렬화를 작은 document 로 한다(static-html.ts).
const toHTML = import.meta.env.SSR ? generateStaticHTML : generateHTML;

export function SurveyBody({ body }: { body: unknown }) {
  const html = useMemo(() => {
    if (!isTiptapDoc(body)) return null;
    try {
      return mirrorTrailingBreaks(toHTML(trimEmptyEdges(body as JSONContent), EXTENSIONS));
    } catch {
      return null;
    }
  }, [body]);

  if (html === null) {
    return <p className="text-sm text-text-muted">본문을 표시할 수 없어요.</p>;
  }

  return (
    <div
      className="tiptap-content break-words"
      // tiptap-generated HTML is sanitized by construction.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * **편집기와 같은 줄 수.** 블록 끝의 `<br>`(Shift+Enter 로 남긴 빈 줄)을 브라우저는 그리지 않는다.
 * ProseMirror 는 그 뒤에 trailing break 를 하나 더 두어 빈 줄로 보이게 한다. 읽기 화면도 같은 규칙을
 * 쓰지 않으면 편집기에서 본 간격보다 한 줄(31.5px) 짧아진다 — 실측 2026-09-18, 이미지 앞 문단.
 */
function mirrorTrailingBreaks(html: string): string {
  return html.replace(/<br>(?=<\/(?:p|h[1-6]|li|blockquote|td|th)>)/g, '<br><br>');
}

/**
 * A paragraph with no real content — empty, or only whitespace text. These render
 * as a visible blank line (`.tiptap-content p:empty::before`), so a stray one at
 * the start/end of the body shows up as awkward breathing room that's hard to
 * delete in the editor.
 */
function isEmptyParagraph(node: unknown): boolean {
  if (typeof node !== 'object' || node === null) return false;
  const n = node as { type?: string; content?: unknown[] };
  if (n.type !== 'paragraph') return false;
  if (!n.content || n.content.length === 0) return true;
  return n.content.every((child) => {
    const c = child as { type?: string; text?: string };
    return c.type === 'text' && (c.text ?? '').trim() === '';
  });
}

/**
 * Trim leading/trailing empty paragraphs from the doc so the body hugs its
 * surroundings (e.g. tags → first image) without injected blank lines.
 * Intentional blank lines *between* content are preserved.
 */
function trimEmptyEdges(doc: JSONContent): JSONContent {
  if (!Array.isArray(doc.content)) return doc;
  let start = 0;
  let end = doc.content.length;
  while (start < end && isEmptyParagraph(doc.content[start])) start += 1;
  while (end > start && isEmptyParagraph(doc.content[end - 1])) end -= 1;
  if (start === 0 && end === doc.content.length) return doc;
  return { ...doc, content: doc.content.slice(start, end) };
}

/** Minimal shape guard for a tiptap ProseMirror doc node. */
function isTiptapDoc(value: unknown): value is { type: string; content?: unknown[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as { type: unknown }).type === 'doc'
  );
}
